import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import App from '@/App.vue'
import router from '@/router'
import { pullAppConfig } from '@/config'
import { useAuthStore } from '@/store/auth'
import '@/styles/global.css'

const app = createApp(App)

// Pinia must be active before we touch any store.
app.use(createPinia())
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  },
})

function showError(message: string) {
  const el = document.getElementById('app')
  if (!el) return
  const p = document.createElement('p')
  p.style.cssText = 'padding: 2rem; font-family: sans-serif; color: #dc2626;'
  p.textContent = message
  el.replaceChildren(p)
}

async function boot() {
  // 1. Load runtime config first — everything downstream depends on it.
  const { errors } = await pullAppConfig()
  if (errors) {
    showError('Failed to load application configuration. Please try refreshing the page.')
    return
  }

  // 2. Initialize auth (Keycloak login-required + role check) BEFORE the router
  //    is installed. Installing the router triggers the initial navigation, and
  //    its guards must see a fully-resolved auth state. Doing this earlier caused
  //    the guard to run during the pre-config phase, throw, next(false), and
  //    cancel the first navigation — leaving a blank router-view.
  const authStore = useAuthStore()
  try {
    await authStore.initialize()
  } catch {
    // On login-required, Keycloak has already redirected the browser to the
    // login page. If we get here it's an authorization failure (e.g. missing
    // super_admin) — surface it instead of a blank screen.
    if (authStore.initError) showError(authStore.initError)
    return
  }

  // 3. Now that auth is ready, install the router and mount. The initial
  //    navigation runs with isInitialized=true, so guards just check state.
  app.use(router)
  await router.isReady()
  app.mount('#app')
}

boot()
